import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import type { DictaphoneSetupAwarenessPhase } from './atoms';
import {
  armDictaphoneSetupAwareness,
  clearDictaphoneSetupAwarenessTrigger,
  dictaphoneSetupAwarenessTriggerAtom,
} from './atoms';

/**
 * Arm the device-setup awareness card when a supported dictaphone connects.
 *
 * Host-injected signals (the app composition owns the hardware snapshot and
 * the flag composition): `deviceKeys` is the reactive set of connected
 * hardware device keys, `visible` is the composed device-phase gate (either
 * phase flag), and `phase` is `setup` when the device-setup gate is on. The
 * first observed key set is treated as the boot snapshot (devices already
 * plugged in when the app started), so in phase 1 the card only arms for a
 * connect *after* that. Phase 2 is a re-offer to existing owners, whose
 * desk-mounted device has no connect moment left, so it ignores the snapshot
 * and lets the arm helper's per-device phase memory and session debounce stop
 * a repeat. Only keys the arm helper ACCEPTS are absorbed into the seed — a
 * device declined because another card was armed stays "new", and the effect
 * re-runs when the armed trigger clears, so it prompts once the armed card is
 * consumed instead of waiting for a physical unplug/replug. A
 * disconnect of the armed device clears the trigger so the card never dangles
 * for an unplugged device, and the flag composition turning off mid-session
 * clears it too, so the kill switch can retract an armed card without a
 * restart.
 */
export const useDictaphoneSetupAwarenessTrigger = ({
  deviceKeys,
  visible,
  phase,
}: {
  deviceKeys: string[];
  visible: boolean;
  phase: DictaphoneSetupAwarenessPhase;
}): void => {
  const armedTrigger = useAtomValue(dictaphoneSetupAwarenessTriggerAtom);

  const seededKeysRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!visible) return;

    const keys = new Set(deviceKeys);
    let seededKeys = seededKeysRef.current;

    if (seededKeys === null) {
      // First observation after the flag turned on — the boot snapshot. A
      // copy, not `keys`: phase 2 adds to the seed while iterating `keys`.
      seededKeys = new Set(keys);
      seededKeysRef.current = seededKeys;
      if (phase === 'detected') return;
    }

    for (const key of keys) {
      // Phase 1 is about the connect moment: a device plugged in before the
      // app started is not a connect event. Phase 2 re-offers to existing
      // owners, so it skips the seed and lets the arm helper suppress repeats.
      if (phase === 'detected' && seededKeys.has(key)) continue;
      if (armDictaphoneSetupAwareness(key, phase)) seededKeys.add(key);
    }
  }, [visible, deviceKeys, armedTrigger, phase]);

  useEffect(() => {
    if (armedTrigger === null) return;
    // The launch row only reads the trigger atom, so clearing it here is what
    // lets a mid-session flag kill retract an already-armed card.
    if (!visible) {
      clearDictaphoneSetupAwarenessTrigger();
      return;
    }
    const stillConnected = deviceKeys.includes(armedTrigger);
    if (!stillConnected) clearDictaphoneSetupAwarenessTrigger();
  }, [deviceKeys, armedTrigger, visible]);
};
